import { type PropsWithChildren, useEffect, useRef } from 'react';

import { getNextFocus } from '@/utils/getNextFocus.ts';

interface AutoAdvanceFormProps {
  autoFocusIndex?: number;
  className?: string;
}

export function AutoAdvanceForm({ children, autoFocusIndex, className }: PropsWithChildren<AutoAdvanceFormProps>) {
  function handleEnterNextField(ev: React.KeyboardEvent<HTMLFormElement>) {
    if (ev.key !== 'Tab' && ev.key !== 'Enter') {
      return;
    }
    ev.preventDefault();

    const next = getNextFocus(ev.currentTarget, ev.target as HTMLElement, ev.shiftKey);
    if (next !== undefined) {
      next.focus();
    } else {
      (ev.target as HTMLElement).blur();
    }
  }

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (autoFocusIndex === undefined) {
      return;
    }

    setTimeout(() => {
      if (formRef.current === null) {
        return;
      }

      let currentElement: HTMLElement = formRef.current;
      for (let i = 0; i < autoFocusIndex + 1; i++) {
        const nextElement = getNextFocus(formRef.current, currentElement);
        if (nextElement === undefined) {
          break;
        }
        currentElement = nextElement;
      }
      currentElement.focus();
    }, 0);
  }, [autoFocusIndex]);

  return (
    <form className={className} ref={formRef} onSubmit={(e) => e.preventDefault()} onKeyDown={handleEnterNextField}>
      {children}
    </form>
  );
}
